import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@codametrix/ui-components';

export type AuditSample = {
  sample_id: number;
  status: string;
  sample_size: number;
  work_item_ids: number[];
  items_reviewed?: number;
  current_f1?: number | null;
  terminal_f1?: number | null;
  failure_mode?: string | null;
};

export type EndAuditResult = {
  sample_id: number;
  status: string;
  failure_mode: string;
  items_reviewed: number;
  items_reverted: number;
};

interface AuditActionBarProps {
  ruleId: string;
  onAuditClosed?: (result: { status: string; failure_mode?: string; items_reviewed?: number } | null) => void;
}

const getAuthHeader = () => sessionStorage.getItem('authToken') ?? '';

const fetchActiveSample = async (ruleId: string): Promise<AuditSample | null> => {
  const res = await fetch(`/api/cqa-service/v1/project/${encodeURIComponent(ruleId)}/audit-sample`, {
    headers: { Accept: 'application/json', Authorization: getAuthHeader() },
  });
  if (res.status === 204) return null;
  if (!res.ok) throw new Error(`fetchActiveSample failed: ${res.status}`);
  return res.json();
};

const postEndAudit = async (sampleId: number): Promise<EndAuditResult> => {
  const res = await fetch(`/api/cqa-service/v1/audit-samples/${sampleId}/end`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: getAuthHeader() },
  });
  if (!res.ok) throw new Error(`endAudit failed: ${res.status}`);
  return res.json();
};

const POLL_INTERVAL_MS = 5000;

export const AuditActionBar: React.FC<AuditActionBarProps> = ({ ruleId, onAuditClosed }) => {
  const [sample, setSample] = useState<AuditSample | null>(null);
  const [ending, setEnding] = useState(false);
  const [failedSample, setFailedSample] = useState<AuditSample | null>(null);
  const closedRef = useRef(false);

  const dispatchClosed = useCallback((detail: object | null) => {
    window.dispatchEvent(new CustomEvent('audit-closed', { detail }));
    onAuditClosed?.(detail as any);
  }, [onAuditClosed]);

  const checkSample = useCallback(async () => {
    if (closedRef.current) return;
    try {
      const s = await fetchActiveSample(ruleId);
      if (s === null) {
        // No sample at all (e.g. all items reviewed and cleared) — treat as a clean close.
        closedRef.current = true;
        dispatchClosed(null);
      } else if (s.status === 'PASSED') {
        closedRef.current = true;
        dispatchClosed({
          sample_id: s.sample_id,
          status: s.status,
          items_reviewed: s.items_reviewed,
          sample_size: s.sample_size,
        });
      } else if (s.status === 'FAILED') {
        closedRef.current = true;
        setFailedSample(s);
      } else {
        setSample(s);
      }
    } catch {
      // non-fatal
    }
  }, [ruleId, dispatchClosed]);

  useEffect(() => {
    checkSample();
    const id = setInterval(checkSample, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [checkSample]);

  useEffect(() => {
    const handler = (e: Event) => {
      const sample = (e as CustomEvent).detail as AuditSample;
      closedRef.current = true;
      setFailedSample(sample);
    };
    window.addEventListener('show-audit-failed', handler);
    return () => window.removeEventListener('show-audit-failed', handler);
  }, []);

  const handleEndAudit = async () => {
    if (!sample) return;
    if (!window.confirm('End audit? Un-reviewed items will revert to Complete.')) return;
    setEnding(true);
    try {
      const result = await postEndAudit(sample.sample_id);
      closedRef.current = true;
      if (result.status === 'FAILED') {
        setFailedSample({ ...sample, status: result.status, failure_mode: result.failure_mode, items_reviewed: result.items_reviewed });
      } else {
        dispatchClosed({ status: result.status, items_reviewed: result.items_reviewed });
      }
    } catch (e) {
      console.error('End audit failed', e);
    } finally {
      setEnding(false);
    }
  };

  const handleAuditFailedDismiss = () => {
    if (failedSample) {
      dispatchClosed({ status: 'FAILED', failure_mode: failedSample.failure_mode ?? undefined, items_reviewed: failedSample.items_reviewed });
    }
    setFailedSample(null);
  };

  const handleAuditFailedBack = () => {
    handleAuditFailedDismiss();
    window.dispatchEvent(new CustomEvent('close-grading-screen'));
  };

  if (failedSample) {
    const f1 = failedSample.current_f1 ?? failedSample.terminal_f1;
    const pct = f1 != null ? `${Math.round(f1 * 100)}%` : '-';
    const reviewed = failedSample.items_reviewed ?? 0;
    const total = failedSample.sample_size ?? failedSample.work_item_ids?.length ?? 0;
    return (
      <Modal title="Audit failed" onClose={handleAuditFailedDismiss}>
        <p>This audit did not meet the 85% agreement threshold.</p>
        <p>Agreement: <strong>{pct}</strong> (required 85%){total > 0 ? ` · ${reviewed} of ${total} items reviewed` : ''}</p>
        <div>
          <button onClick={handleAuditFailedBack} className="chakra-secondary-button">
            Back to Worklist
          </button>
        </div>
      </Modal>
    );
  }

  if (!sample) return null;

  const total = sample.sample_size ?? sample.work_item_ids?.length ?? 0;
  const reviewed = sample.items_reviewed ?? 0;
  const f1 = sample.current_f1;
  const accuracyPct = f1 != null ? Math.round(f1 * 100) : null;

  return (
    <>
      <div className="cmx-chip case-status-in-progress action-bar-button">
        Audit · {reviewed} of {total} · Accuracy {accuracyPct != null ? `${accuracyPct}%` : '-'}
      </div>
      <button
        onClick={handleEndAudit}
        disabled={ending}
        className="chakra-secondary-button prev-next-button action-bar-button">
        End Audit
      </button>
    </>
  );
};
